/**
 * ZoneProfilesPage — `/sensors/zone-profiles`.
 *
 * Edit per-zone agronomic descriptors (soil_type, slope, nozzle_inhr,
 * root_depth_in, area_sqft, sun_exposure) outside the Schedule Builder
 * flow. These feed the Water Budget runtime math + cycle-and-soak split
 * recommendations. Each zone shows a "Profile incomplete" badge until
 * every field is filled.
 *
 * Backend:
 *   GET   /api/irrigation/schedules/zone-profiles
 *   PATCH /api/irrigation/schedules/zone-profiles/{schedule_id}/{rachio_zone_id}
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, Save, AlertCircle, CheckCircle2 } from 'lucide-react';
import { API_URL } from '../config/api';

const SOIL_OPTIONS = [
  { value: 'sandy', label: 'Sandy' },
  { value: 'sandy_loam', label: 'Sandy loam' },
  { value: 'loam', label: 'Loam' },
  { value: 'silty_loam', label: 'Silty loam' },
  { value: 'clay', label: 'Clay' },
  { value: 'silty_clay', label: 'Silty clay' },
];
const SLOPE_OPTIONS = [
  { value: 'flat', label: 'Flat' },
  { value: 'gentle', label: 'Gentle' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'steep', label: 'Steep' },
];
const SUN_OPTIONS = [
  { value: 'full_sun', label: 'Full sun (6+ hrs)' },
  { value: 'part_sun', label: 'Part sun (3–6 hrs)' },
  { value: 'shade', label: 'Shade (<3 hrs)' },
];

const inputClass = 'w-full bg-slate-950 border border-slate-700 rounded-lg px-2 py-1.5 text-sm text-slate-100 focus:outline-none focus:border-emerald-500';

function ZoneCard({ zone, onSave }) {
  const [form, setForm] = useState({
    soil_type: zone.soil_type || '',
    slope: zone.slope || '',
    nozzle_inhr: zone.nozzle_inhr ?? '',
    root_depth_in: zone.root_depth_in ?? '',
    area_sqft: zone.area_sqft ?? '',
    sun_exposure: zone.sun_exposure || '',
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        soil_type: form.soil_type || null,
        slope: form.slope || null,
        nozzle_inhr: form.nozzle_inhr === '' ? null : Number(form.nozzle_inhr),
        root_depth_in: form.root_depth_in === '' ? null : Number(form.root_depth_in),
        area_sqft: form.area_sqft === '' ? null : Number(form.area_sqft),
        sun_exposure: form.sun_exposure || null,
      };
      const url = `${API_URL}/api/irrigation/schedules/zone-profiles/${zone.schedule_id}/${zone.rachio_zone_id}`;
      const res = await axios.patch(url, payload, { withCredentials: true });
      toast.success(`Saved · ${res.data.profile_complete ? 'profile complete' : 'still incomplete'}`);
      onSave?.(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save zone profile');
    } finally {
      setSaving(false);
    }
  };

  const complete = zone.profile_complete;

  return (
    <div
      data-testid={`zone-profile-card-${zone.rachio_zone_id}`}
      className={`bg-slate-900/60 border ${complete ? 'border-emerald-900/60' : 'border-amber-900/60'} rounded-2xl p-4 space-y-3`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-base font-semibold text-slate-100 truncate">{zone.name}</div>
          <div className="text-[11px] text-slate-500 truncate">
            in schedule: <span className="text-slate-400">{zone.schedule_name}</span>
          </div>
        </div>
        {complete ? (
          <span
            data-testid={`zone-profile-badge-complete-${zone.rachio_zone_id}`}
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-emerald-300 bg-emerald-950/60 border border-emerald-800/60 rounded-full px-2 py-0.5"
          >
            <CheckCircle2 className="w-3 h-3" />
            Complete
          </span>
        ) : (
          <span
            data-testid={`zone-profile-badge-incomplete-${zone.rachio_zone_id}`}
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-amber-300 bg-amber-950/60 border border-amber-800/60 rounded-full px-2 py-0.5"
          >
            <AlertCircle className="w-3 h-3" />
            Incomplete
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold block mb-1">Soil type</label>
          <select
            data-testid={`zone-profile-soil-${zone.rachio_zone_id}`}
            value={form.soil_type}
            onChange={(e) => set('soil_type', e.target.value)}
            className={inputClass}
          >
            <option value="">— Select —</option>
            {SOIL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold block mb-1">Slope</label>
          <select
            data-testid={`zone-profile-slope-${zone.rachio_zone_id}`}
            value={form.slope}
            onChange={(e) => set('slope', e.target.value)}
            className={inputClass}
          >
            <option value="">— Select —</option>
            {SLOPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold block mb-1">Sun exposure</label>
          <select
            data-testid={`zone-profile-sun-${zone.rachio_zone_id}`}
            value={form.sun_exposure}
            onChange={(e) => set('sun_exposure', e.target.value)}
            className={inputClass}
          >
            <option value="">— Select —</option>
            {SUN_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold block mb-1">Nozzle (in/hr)</label>
          <input
            type="number" step="0.05" min="0.1" max="4.0"
            data-testid={`zone-profile-nozzle-${zone.rachio_zone_id}`}
            value={form.nozzle_inhr}
            onChange={(e) => set('nozzle_inhr', e.target.value)}
            placeholder="e.g. 0.5"
            className={inputClass}
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold block mb-1">Root depth (in)</label>
          <input
            type="number" step="0.5" min="1" max="24"
            data-testid={`zone-profile-rootdepth-${zone.rachio_zone_id}`}
            value={form.root_depth_in}
            onChange={(e) => set('root_depth_in', e.target.value)}
            placeholder="e.g. 6"
            className={inputClass}
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold block mb-1">Area (sq ft)</label>
          <input
            type="number" step="10" min="10" max="100000"
            data-testid={`zone-profile-area-${zone.rachio_zone_id}`}
            value={form.area_sqft}
            onChange={(e) => set('area_sqft', e.target.value)}
            placeholder="e.g. 1200"
            className={inputClass}
          />
        </div>
      </div>

      <div className="flex justify-end">
        <button
          data-testid={`zone-profile-save-${zone.rachio_zone_id}`}
          disabled={saving}
          onClick={save}
          className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm font-semibold px-3 py-1.5 rounded-lg"
        >
          <Save className="w-4 h-4" />
          Save profile
        </button>
      </div>
    </div>
  );
}

export default function ZoneProfilesPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API_URL}/api/irrigation/schedules/zone-profiles`,
        { withCredentials: true },
      );
      setData(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onZoneSaved = (patched) => {
    setData((prev) => {
      if (!prev) return prev;
      const zones = prev.zones.map((z) =>
        z.rachio_zone_id === patched.rachio_zone_id
          ? { ...z, ...patched }
          : z
      );
      const complete = zones.filter((z) => z.profile_complete).length;
      return {
        ...prev,
        zones,
        complete_count: complete,
        incomplete_count: zones.length - complete,
      };
    });
  };

  return (
    <div className="min-h-screen bg-black text-slate-100 pb-24">
      <div className="max-w-md mx-auto px-4 pt-3">
        <div className="flex items-center justify-between py-2">
          <button
            data-testid="zone-profiles-back"
            onClick={() => navigate('/profile')}
            className="p-2 -ml-2 rounded-full hover:bg-slate-800"
          >
            <ChevronLeft className="w-6 h-6 text-slate-200" />
          </button>
          <div className="text-base font-semibold tracking-tight">Zone profiles</div>
          <span className="w-9" />
        </div>

        <div className="text-xs text-slate-400 px-1 mb-3 leading-snug">
          Soil, slope, nozzle rate, root depth, area, and sun exposure feed the
          Water Budget runtime math and cycle-and-soak split recommendations.
          Missing fields = the engine falls back to global defaults.
        </div>

        {err && (
          <div
            data-testid="zone-profiles-error"
            className="bg-rose-900/40 border border-rose-700/40 rounded-lg p-3 text-sm text-rose-300"
          >
            Failed to load zone profiles: {err}
          </div>
        )}

        {!data && !err && (
          <div className="text-sm text-slate-400 animate-pulse">Loading…</div>
        )}

        {data && data.zones.length === 0 && (
          <div
            data-testid="zone-profiles-empty"
            className="text-center text-sm text-slate-400 py-10 bg-slate-900/60 border border-slate-800 rounded-2xl"
          >
            No zones yet — connect Rachio and create a schedule first.
          </div>
        )}

        {data && data.zones.length > 0 && (
          <>
            <div
              data-testid="zone-profiles-summary"
              className="bg-slate-900/60 border border-slate-800 rounded-2xl p-3 mb-3 text-xs text-slate-300"
            >
              <span className="text-emerald-300 font-semibold">{data.complete_count}</span>{' '}
              complete · <span className="text-amber-300 font-semibold">{data.incomplete_count}</span>{' '}
              incomplete · <span className="text-slate-200 font-semibold">{data.count}</span> total
            </div>
            <div className="space-y-3" data-testid="zone-profiles-list">
              {data.zones.map((z) => (
                <ZoneCard
                  key={z.rachio_zone_id}
                  zone={z}
                  onSave={onZoneSaved}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
